// RosterSystem — weekly form fluctuation and market refresh, monthly
// player progression. Player salaries are paid by EconomySystem.

import { EventBus, GameEvents } from '../utils/EventBus'
import { useGameStore } from '@/store/gameStore'
import { FEATURES, isFeatureUnlocked } from '@/config/clubLevelConfig'
import { generatePlayer, applyLevelToStats, ROSTER_RULES, Player } from '@/config/playersConfig'
import { getCoachEffects, getMedicalEffects } from './staffEffects'
import { Rng, defaultRng, rollInt } from '../utils/rng'

const MONTHS_PER_YEAR = 12
const DECLINE_AGE = 33

export class RosterSystem {
  private dayHandler = () => this.seedMarketIfEmpty()
  private weekHandler = () => {
    this.fluctuateForm()
    this.refreshMarket()
    // Snapshot AFTER the form fluctuation so the ficha history shows the
    // week's final state
    useGameStore.getState().recordPlayerSnapshots()
  }
  private monthHandler = () => this.progressPlayers()

  constructor(private rng: Rng = defaultRng) {
    EventBus.on(GameEvents.DAY_CHANGED, this.dayHandler)
    EventBus.on(GameEvents.WEEK_CHANGED, this.weekHandler)
    EventBus.on(GameEvents.MONTH_CHANGED, this.monthHandler)
  }

  destroy() {
    EventBus.off(GameEvents.DAY_CHANGED, this.dayHandler)
    EventBus.off(GameEvents.WEEK_CHANGED, this.weekHandler)
    EventBus.off(GameEvents.MONTH_CHANGED, this.monthHandler)
  }

  /** First fill: populate the market the day the feature unlocks */
  private seedMarketIfEmpty() {
    const store = useGameStore.getState()
    if (store.transferMarket.length > 0) return
    if (!isFeatureUnlocked(FEATURES.PLAYER_ROSTER, store.clubLevel)) return
    this.refreshMarket()
  }

  /** Form regresses towards 50 with random noise, clamped to [0, 100].
   *  A sports psychologist puts a floor under the bad streaks. */
  private fluctuateForm() {
    const store = useGameStore.getState()
    if (store.players.length === 0) return

    const { formFloor } = getMedicalEffects(store.staff)

    const updated = store.players.map(p => {
      const regression = (50 - p.form) * 0.2
      const noise = rollInt(-10, 10, this.rng)
      const form = Math.max(formFloor, Math.min(100, Math.round(p.form + regression + noise)))
      return { ...p, form }
    })
    store.setPlayers(updated)
  }

  /** Replace the transfer market with fresh candidates each week */
  private refreshMarket() {
    const store = useGameStore.getState()
    if (!isFeatureUnlocked(FEATURES.PLAYER_ROSTER, store.clubLevel)) return

    const candidates: Player[] = []
    for (let i = 0; i < ROSTER_RULES.marketSize; i++) {
      candidates.push(generatePlayer(store.clubLevel, store.day, this.rng))
    }
    store.setTransferMarket(candidates)
  }

  /**
   * Monthly progression: young players grow towards their potential
   * (boosted by the best coach's skill), veterans decline. Players age
   * one year every 12 game months.
   */
  private progressPlayers() {
    const store = useGameStore.getState()
    if (store.players.length === 0) return

    // 50% base growth chance, up to +50% with a top coach; an elite
    // tactician (táctica ≥ 70) raises the growth roll ceiling to 3
    const { growthChance, growthRollMax } = getCoachEffects(store.staff)
    const hasBirthday = store.month % MONTHS_PER_YEAR === 0

    const updated = store.players.map(p => {
      const age = hasBirthday ? p.age + 1 : p.age
      let level = p.level

      if (age >= DECLINE_AGE) {
        level = Math.max(1, level - rollInt(0, 1, this.rng))
      } else if (level < p.potential && this.rng() < growthChance) {
        level = Math.min(p.potential, level + rollInt(1, growthRollMax, this.rng))
      }

      if (level === p.level && age === p.age) return p
      // Keep the stats ↔ level invariant: nudge stats to the new mean
      const stats = level === p.level ? p.stats : applyLevelToStats(p.stats, level, this.rng)
      return { ...p, age, level, stats }
    })
    store.setPlayers(updated)
  }
}
